// React Query hooks for professor panel

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as professorService from '../services/professorService';
import type {
  GetMyClassesQueryDto,
  GetAttendanceQueryDto,
  CreateGradeConfigDto,
  SaveGradesDto,
  MarkAttendanceDto,
  UpdateAttendanceDto,
  UploadSyllabusDto,
  UpdateSyllabusDto,
} from '@/types/professor.types';

// ==================== Query Keys ====================

export const professorKeys = {
  all: ['professor'] as const,
  dashboard: () => [...professorKeys.all, 'dashboard'] as const,

  // My Classes
  classes: () => [...professorKeys.all, 'classes'] as const,
  classesList: (query: GetMyClassesQueryDto) => [...professorKeys.classes(), 'list', query] as const,
  classDetail: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.classes(), 'detail', groupeId, moduleElementId] as const,
  classStudents: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.classes(), 'students', groupeId, moduleElementId] as const,

  // Grades
  grades: () => [...professorKeys.all, 'grades'] as const,
  gradeConfig: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.grades(), 'config', groupeId, moduleElementId] as const,
  gradeTemplates: () => [...professorKeys.grades(), 'templates'] as const,
  studentGrades: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.grades(), 'students', groupeId, moduleElementId] as const,

  // Attendance
  attendance: () => [...professorKeys.all, 'attendance'] as const,

  // Syllabus
  syllabus: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.all, 'syllabus', groupeId, moduleElementId] as const,
  announcements: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.all, 'announcements', groupeId, moduleElementId] as const,
  content: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.all, 'content', groupeId, moduleElementId] as const,
  assignments: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.all, 'assignments', groupeId, moduleElementId] as const,
  attendanceHistory: (groupeId: string, moduleElementId: string, query: GetAttendanceQueryDto) =>
    [...professorKeys.attendance(), 'history', groupeId, moduleElementId, query] as const,
  attendanceSession: (groupeId: string, moduleElementId: string, date: string) =>
    [...professorKeys.attendance(), 'session', groupeId, moduleElementId, date] as const,
  attendanceStats: (groupeId: string, moduleElementId: string) =>
    [...professorKeys.attendance(), 'stats', groupeId, moduleElementId] as const,
};

// ==================== Dashboard Hooks ====================

export function useProfessorDashboard() {
  return useQuery({
    queryKey: professorKeys.dashboard(),
    queryFn: professorService.fetchProfessorDashboard,
    staleTime: 30000, // 30 seconds
  });
}

// ==================== My Classes Hooks ====================

export function useMyClasses(query: GetMyClassesQueryDto = {}) {
  return useQuery({
    queryKey: professorKeys.classesList(query),
    queryFn: () => professorService.fetchMyClasses(query),
    staleTime: 30000,
  });
}

export function useClassDetail(groupeId: string | null, moduleElementId: string | null, academicYearId?: string) {
  return useQuery({
    queryKey: professorKeys.classDetail(groupeId || '', moduleElementId || ''),
    queryFn: () => professorService.fetchClassDetail(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useClassStudents(groupeId: string | null, moduleElementId: string | null, academicYearId?: string) {
  return useQuery({
    queryKey: [...professorKeys.classStudents(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchClassStudents(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

// ==================== Grade Configuration Hooks ====================

export function useGradeConfig(
  groupeId: string | null,
  moduleElementId: string | null,
  academicYearId?: string
) {
  return useQuery({
    queryKey: [...professorKeys.gradeConfig(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchGradeConfig(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 60000, // 1 minute
  });
}

export function useGradeTemplates() {
  return useQuery({
    queryKey: professorKeys.gradeTemplates(),
    queryFn: professorService.fetchGradeTemplates,
    staleTime: 300000, // 5 minutes - templates don't change often
  });
}

export function useSaveGradeConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: CreateGradeConfigDto;
    }) => professorService.saveGradeConfig(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: professorKeys.gradeConfig(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({
        queryKey: professorKeys.studentGrades(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useDeleteGradeComponent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configId: string) => professorService.deleteGradeComponent(configId),
    onSuccess: () => {
      // Invalidate all grade-related queries
      queryClient.invalidateQueries({ queryKey: professorKeys.grades() });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useApplyGradeTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      templateId,
    }: {
      groupeId: string;
      moduleElementId: string;
      templateId: string;
    }) => professorService.applyGradeTemplate(groupeId, moduleElementId, templateId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.gradeConfig(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({
        queryKey: professorKeys.studentGrades(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

// ==================== Grade Entry Hooks ====================

export function useStudentGrades(
  groupeId: string | null,
  moduleElementId: string | null,
  academicYearId?: string
) {
  return useQuery({
    queryKey: [professorKeys.studentGrades(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchStudentGrades(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useSaveGrades() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: SaveGradesDto;
    }) => professorService.saveGrades(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.studentGrades(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
      queryClient.invalidateQueries({ queryKey: professorKeys.dashboard() });
    },
  });
}

export function useFinalizeGrades() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
    }: {
      groupeId: string;
      moduleElementId: string;
    }) => professorService.finalizeGrades(groupeId, moduleElementId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.gradeConfig(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({
        queryKey: professorKeys.studentGrades(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
      queryClient.invalidateQueries({ queryKey: professorKeys.dashboard() });
      // Invalidate rattrapage check so the tab appears automatically
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'has-rattrapages', groupeId, moduleElementId],
      });
      // Invalidate rattrapage data so it loads immediately when tab is opened
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'rattrapages', groupeId, moduleElementId],
      });
    },
  });
}

export function useUnfinalizeGrades() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
    }: {
      groupeId: string;
      moduleElementId: string;
    }) => professorService.unfinalizeGrades(groupeId, moduleElementId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.gradeConfig(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({
        queryKey: professorKeys.studentGrades(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
      queryClient.invalidateQueries({ queryKey: professorKeys.dashboard() });
      // Invalidate rattrapage check
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'has-rattrapages', groupeId, moduleElementId],
      });
      // Invalidate rattrapage data
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'rattrapages', groupeId, moduleElementId],
      });
    },
  });
}

// ==================== Attendance Hooks ====================

export function useScheduledSessions(
  groupeId: string | null,
  moduleElementId: string | null,
  academicYearId?: string
) {
  return useQuery({
    queryKey: [...[...professorKeys.attendance(), 'scheduled-sessions', groupeId, moduleElementId], academicYearId],
    queryFn: () => professorService.fetchScheduledSessions(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useAttendanceHistory(
  groupeId: string | null,
  moduleElementId: string | null,
  query: GetAttendanceQueryDto = {}
) {
  return useQuery({
    queryKey: professorKeys.attendanceHistory(groupeId || '', moduleElementId || '', query),
    queryFn: () => professorService.fetchAttendanceHistory(groupeId!, moduleElementId!, query),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useAttendanceForSession(
  groupeId: string | null,
  moduleElementId: string | null,
  scheduleSessionId: string | null
) {
  return useQuery({
    queryKey: professorKeys.attendanceSession(groupeId || '', moduleElementId || '', scheduleSessionId || ''),
    queryFn: () => professorService.fetchAttendanceForSession(groupeId!, moduleElementId!, scheduleSessionId!),
    enabled: !!groupeId && !!moduleElementId && !!scheduleSessionId,
    staleTime: 30000,
  });
}

export function useAttendanceStats(
  groupeId: string | null,
  moduleElementId: string | null,
  academicYearId?: string
) {
  return useQuery({
    queryKey: [professorKeys.attendanceStats(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchAttendanceStats(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useMarkAttendance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: MarkAttendanceDto;
    }) => professorService.markAttendance(groupeId, moduleElementId, dto),
    onSuccess: () => {
      // Invalidate all attendance-related queries for this class
      queryClient.invalidateQueries({
        queryKey: professorKeys.attendance(),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
      queryClient.invalidateQueries({ queryKey: professorKeys.dashboard() });
    },
  });
}

export function useUpdateAttendance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attendanceId,
      dto,
    }: {
      attendanceId: string;
      dto: UpdateAttendanceDto;
    }) => professorService.updateAttendance(attendanceId, dto),
    onSuccess: () => {
      // Invalidate all attendance queries
      queryClient.invalidateQueries({ queryKey: professorKeys.attendance() });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

// ==================== Syllabus Hooks ====================

// A syllabus belongs to one (group, module) pair: two groups never share one.
export function useSyllabus(groupeId: string | null, moduleElementId: string | null, academicYearId?: string) {
  return useQuery({
    queryKey: [...professorKeys.syllabus(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchSyllabus(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 60000, // 1 minute
  });
}

interface SyllabusMutationVars {
  groupeId: string;
  moduleElementId: string;
}

export function useUploadSyllabus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupeId, moduleElementId, dto }: SyllabusMutationVars & { dto: UploadSyllabusDto }) =>
      professorService.uploadSyllabus(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.syllabus(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useUpdateSyllabus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupeId, moduleElementId, dto }: SyllabusMutationVars & { dto: UpdateSyllabusDto }) =>
      professorService.updateSyllabus(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.syllabus(groupeId, moduleElementId),
      });
    },
  });
}

export function useDeleteSyllabus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupeId, moduleElementId }: SyllabusMutationVars) =>
      professorService.deleteSyllabus(groupeId, moduleElementId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      // Clear every cached variant (per consulted year) so the file leaves the
      // screen at once, then refetch to be sure
      queryClient.setQueriesData({ queryKey: professorKeys.syllabus(groupeId, moduleElementId) }, null);
      queryClient.invalidateQueries({
        queryKey: professorKeys.syllabus(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

// ==================== Course Content Hooks ====================

// Course content (chapters, materials) is one space per (group, module) pair.
export function useCourseContent(groupeId: string | null, moduleElementId: string | null, academicYearId?: string) {
  return useQuery({
    queryKey: [...professorKeys.content(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchCourseContent(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useUpdateContentSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      generalSectionTitle,
    }: {
      groupeId: string;
      moduleElementId: string;
      generalSectionTitle: string;
    }) => professorService.updateContentSettings(groupeId, moduleElementId, generalSectionTitle),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
    },
  });
}

export function useCreateChapter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').CreateChapterDto;
    }) => professorService.createChapter(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useUpdateChapter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chapterId,
      dto,
    }: {
      chapterId: string;
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').UpdateChapterDto;
    }) => professorService.updateChapter(chapterId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
    },
  });
}

export function useDeleteChapter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chapterId,
    }: {
      chapterId: string;
      groupeId: string;
      moduleElementId: string;
    }) => professorService.deleteChapter(chapterId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useReorderChapters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      chapterIds,
    }: {
      groupeId: string;
      moduleElementId: string;
      chapterIds: string[];
    }) => professorService.reorderChapters(groupeId, moduleElementId, chapterIds),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
    },
  });
}

export function useCreateMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').CreateMaterialDto;
    }) => professorService.createMaterial(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useUpdateMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      materialId,
      dto,
    }: {
      materialId: string;
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').UpdateMaterialDto;
    }) => professorService.updateMaterial(materialId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
    },
  });
}

export function useDeleteMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      materialId,
    }: {
      materialId: string;
      groupeId: string;
      moduleElementId: string;
    }) => professorService.deleteMaterial(materialId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useMoveMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      materialId,
      targetChapterId,
    }: {
      materialId: string;
      groupeId: string;
      moduleElementId: string;
      targetChapterId: string | null;
    }) => professorService.moveMaterial(materialId, targetChapterId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.content(groupeId, moduleElementId),
      });
    },
  });
}

// ==================== Announcement Hooks ====================

// Announcements are one feed per (group, module) pair.
export function useAnnouncements(groupeId: string | null, moduleElementId: string | null, academicYearId?: string) {
  return useQuery({
    queryKey: [...professorKeys.announcements(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchAnnouncements(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').CreateAnnouncementDto;
    }) => professorService.createAnnouncement(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.announcements(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      announcementId,
      dto,
    }: {
      announcementId: string;
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').UpdateAnnouncementDto;
    }) => professorService.updateAnnouncement(announcementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.announcements(groupeId, moduleElementId),
      });
    },
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      announcementId,
    }: {
      announcementId: string;
      groupeId: string;
      moduleElementId: string;
    }) => professorService.deleteAnnouncement(announcementId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.announcements(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useToggleAnnouncementPin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      announcementId,
    }: {
      announcementId: string;
      groupeId: string;
      moduleElementId: string;
    }) => professorService.toggleAnnouncementPin(announcementId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.announcements(groupeId, moduleElementId),
      });
    },
  });
}

// ==================== Assignment Hooks ====================

// Assignments (devoirs) are one list per (group, module) pair.
export function useAssignments(groupeId: string | null, moduleElementId: string | null, academicYearId?: string) {
  return useQuery({
    queryKey: [...professorKeys.assignments(groupeId || '', moduleElementId || ''), academicYearId],
    queryFn: () => professorService.fetchAssignments(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useAssignmentDetail(assignmentId: string | null) {
  return useQuery({
    queryKey: [...professorKeys.all, 'assignments', 'detail', assignmentId],
    queryFn: () => professorService.fetchAssignmentDetail(assignmentId!),
    enabled: !!assignmentId,
    staleTime: 30000,
  });
}

export function useCreateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').CreateAssignmentDto;
    }) => professorService.createAssignment(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.assignments(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assignmentId,
      dto,
    }: {
      assignmentId: string;
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').UpdateAssignmentDto;
    }) => professorService.updateAssignment(assignmentId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.assignments(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'assignments', 'detail'],
      });
    },
  });
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assignmentId,
    }: {
      assignmentId: string;
      groupeId: string;
      moduleElementId: string;
    }) => professorService.deleteAssignment(assignmentId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: professorKeys.assignments(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useGradeSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      submissionId,
      dto,
    }: {
      submissionId: string;
      assignmentId: string;
      groupeId: string;
      moduleElementId: string;
      dto: import('@/types/professor.types').GradeSubmissionDto;
    }) => professorService.gradeSubmission(submissionId, dto),
    onSuccess: (_, { assignmentId, groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'assignments', 'detail', assignmentId],
      });
      queryClient.invalidateQueries({
        queryKey: professorKeys.assignments(groupeId, moduleElementId),
      });
    },
  });
}

// ==================== Rattrapage Hooks ====================

export function useStudentsWithRattrapage(
  groupeId: string | null,
  moduleElementId: string | null,
  academicYearId?: string
) {
  return useQuery({
    queryKey: [...professorKeys.all, 'rattrapages', groupeId, moduleElementId],
    queryFn: () => professorService.fetchStudentsWithRattrapage(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useHasStudentsWithRattrapage(
  groupeId: string | null,
  moduleElementId: string | null,
  academicYearId?: string
) {
  return useQuery({
    queryKey: [...professorKeys.all, 'has-rattrapages', groupeId, moduleElementId],
    queryFn: () => professorService.checkHasStudentsWithRattrapage(groupeId!, moduleElementId!, academicYearId),
    enabled: !!groupeId && !!moduleElementId,
    staleTime: 30000,
  });
}

export function useFinalizeRattrapageGrades() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupeId, moduleElementId }: { groupeId: string; moduleElementId: string }) =>
      professorService.finalizeRattrapageGrades(groupeId, moduleElementId),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'rattrapages', groupeId, moduleElementId],
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
    },
  });
}

export function useSaveRattrapageGrades() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupeId,
      moduleElementId,
      dto,
    }: {
      groupeId: string;
      moduleElementId: string;
      dto: { grades: { studentId: string; rattrapageGrade: number; comments?: string }[] };
    }) => professorService.saveRattrapageGrades(groupeId, moduleElementId, dto),
    onSuccess: (_, { groupeId, moduleElementId }) => {
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'rattrapages', groupeId, moduleElementId],
      });
      queryClient.invalidateQueries({
        queryKey: [...professorKeys.all, 'has-rattrapages', groupeId, moduleElementId],
      });
      queryClient.invalidateQueries({
        queryKey: professorKeys.studentGrades(groupeId, moduleElementId),
      });
      queryClient.invalidateQueries({ queryKey: professorKeys.classes() });
      queryClient.invalidateQueries({ queryKey: professorKeys.dashboard() });
    },
  });
}

export function useMyExams(academicYearId?: string) {
  return useQuery({
    queryKey: [...professorKeys.all, 'exams', academicYearId ?? 'current'],
    queryFn: () => professorService.fetchMyExams(academicYearId),
    staleTime: 30000,
  });
}
